import { Reveal } from "./Reveal";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Smile, Stethoscope, Gem, Layers, HeartPulse } from "lucide-react";

const CARE = [
  {
    icon: Smile,
    title: "Complete & Removable Dentures",
    points: [
      "Remove your dentures at night and soak them in clean water or a denture-cleaning solution.",
      "Clean daily with a soft brush and mild soap — avoid abrasive toothpaste.",
      "Rinse your mouth and gently massage the gums to keep tissues healthy.",
      "Mild soreness or extra saliva is normal at first; return for adjustment if any sore spot persists.",
      "Start with soft foods, chew on both sides, and avoid very hard or sticky items.",
    ],
  },
  {
    icon: Stethoscope,
    title: "Dental Implants (after surgery)",
    points: [
      "Bite gently on the gauze for 30–45 minutes; slight bleeding within the first 24 hours is normal.",
      "Use a cold compress on the cheek to reduce swelling; do not spit or rinse vigorously for 24 hours.",
      "Keep to a soft, lukewarm diet for a few days and avoid chewing directly on the surgical site.",
      "Do not smoke or use straws — this can disturb healing of the implant.",
      "Take medicines as prescribed and begin gentle warm-saline rinses after 24 hours.",
    ],
  },
  {
    icon: Gem,
    title: "Crowns, Bridges & Veneers",
    points: [
      "Mild sensitivity to hot or cold for a few days is normal and usually settles.",
      "Avoid biting very hard objects and never use your teeth as tools.",
      "Brush and floss well — use a floss threader or interdental brush under bridges.",
      "Report any 'high' bite, looseness, or persistent discomfort promptly.",
    ],
  },
  {
    icon: Layers,
    title: "Full-Mouth Rehabilitation & Night Guard",
    points: [
      "Wear your night guard / occlusal splint as advised to protect the new restorations.",
      "Adapt to the new bite gradually; report any pressure points you feel.",
      "Attend regular reviews and professional cleaning every six months.",
    ],
  },
  {
    icon: HeartPulse,
    title: "General Care — for everyone",
    points: [
      "Maintain excellent oral hygiene: brush twice daily and floss once a day.",
      "Keep your recall appointments to maximise the life of your prostheses.",
      "Contact the clinic promptly for any pain, swelling, or issue with your prosthesis.",
    ],
  },
];

export const PostCare = () => {
  return (
    <section id="aftercare" data-testid="aftercare-section" className="bg-ivory py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="text-champagne-dark text-xs uppercase tracking-[0.3em] mb-6">
              Post-Care Instructions
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight text-emerald leading-[1.05]">
              Caring for your new smile.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-emerald/60 leading-relaxed">
              Simple, practical aftercare guidance to protect your treatment and keep it looking and
              feeling its best. Always follow the specific advice given to you by Dr. Mishra.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {CARE.map((c, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`aftercare-item-${i}`}
                  className="border-b border-emerald/15"
                >
                  <AccordionTrigger className="py-6 hover:no-underline group">
                    <span className="flex items-center gap-4 text-left">
                      <span className="h-11 w-11 shrink-0 flex items-center justify-center bg-emerald/5 text-champagne-dark group-hover:bg-emerald group-hover:text-champagne transition-colors duration-300">
                        <c.icon size={20} />
                      </span>
                      <span className="font-display text-xl md:text-2xl text-emerald tracking-tight">
                        {c.title}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="pl-[60px] pb-4 space-y-3">
                      {c.points.map((p, j) => (
                        <li key={j} className="flex gap-3 text-emerald/70 text-sm md:text-base leading-relaxed">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-champagne shrink-0" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default PostCare;
